@if (error(); as e) { <div class="alert alert-warning">{{ ('companion.errors.' + e) | translate }}</div> }

<!-- 1 · Age check -->
@if (!profile()?.age_verified) {
  <div class="cp-card">
    <h2 class="cp-h">{{ 'companion.profile.age.title' | translate }}</h2>
    <p class="cp-hint">{{ 'companion.profile.age.hint' | translate }}</p>
    <div class="grid2">
      <label class="cp-field">
        <span>{{ 'companion.profile.age.dob' | translate }}</span>
        <input class="form-control" type="date" [ngModel]="dob()" (ngModelChange)="dob.set($event)" />
      </label>
      <label class="cp-field">
        <span>{{ 'companion.profile.settings.language' | translate }}</span>
        <select class="form-select" [ngModel]="language()" (ngModelChange)="language.set($event)">
          <option value="de">Deutsch</option><option value="en">English</option>
        </select>
      </label>
      <label class="cp-field">
        <span>{{ 'companion.profile.settings.time_zone' | translate }}</span>
        <select class="form-select" [ngModel]="timeZone()" (ngModelChange)="timeZone.set($event)">
          @if (!zones.includes(browserZone)) { <option [value]="browserZone">{{ browserZone }}</option> }
          @for (z of zones; track z) { <option [value]="z">{{ z }}</option> }
        </select>
      </label>
    </div>
    <label class="d-block mt-2"><input type="checkbox" class="form-check-input me-2" [checked]="dobConfirm()" (change)="dobConfirm.set(!dobConfirm())" />{{ 'companion.profile.age.confirm' | translate }}</label>
    <p class="cp-hint mt-2">{{ 'companion.profile.age.consent' | translate }}</p>
    <div class="cp-actions">
      <button cButton color="primary" [disabled]="busy() || !dob() || !dobConfirm()" (click)="submitAge()">{{ 'companion.continue' | translate }}</button>
    </div>
  </div>
}

<!-- 2 · Guardian consent for a minor -->
@if (profile()?.minor) {
  <div class="cp-card">
    <h2 class="cp-h">{{ 'companion.guardian.title' | translate }}</h2>
    <p>{{ 'companion.guardian.text' | translate }}</p>
    @if (profile()?.guardian; as g) {
      <p class="mb-1"><strong>{{ g.name }}</strong> ({{ ('companion.guardian.relations.' + (g.relation || 'other')) | translate }}) · {{ g.email }}</p>
      <p class="mb-1"><span class="cp-chip" [class.cp-chip--ok]="g.status === 'confirmed'" [class.cp-chip--warn]="g.status !== 'confirmed'">{{ ('companion.guardian.status.' + g.status) | translate }}</span>
        @if (g.status === 'pending' && g.expires_at) { <span class="cp-hint ms-1">{{ 'companion.guardian.expires' | translate:{ date: (g.expires_at | date:'mediumDate':undefined:lang()) } }}</span> }
        @if (g.confirmed_at) { <span class="cp-hint ms-1">{{ g.confirmed_at | date:'medium':undefined:lang() }}</span> }
      </p>
      @if (!readOnly()) {
        <div class="cp-actions">
          <button cButton color="secondary" variant="outline" size="sm" [disabled]="busy()" (click)="revokeGuardian()">{{ 'companion.guardian.revoke' | translate }}</button>
        </div>
      }
    }
    @if (!profile()?.guardian || profile()?.guardian?.status === 'declined' || profile()?.guardian?.status === 'expired') {
      <div class="grid2 mt-2">
        <label class="cp-field"><span>{{ 'companion.guardian.name' | translate }}</span><input class="form-control" type="text" [ngModel]="gName()" (ngModelChange)="gName.set($event)" /></label>
        <label class="cp-field"><span>{{ 'companion.guardian.relation' | translate }}</span>
          <select class="form-select" [ngModel]="gRelation()" (ngModelChange)="gRelation.set($event)">
            @for (r of ['mother', 'father', 'guardian', 'other']; track r) { <option [value]="r">{{ ('companion.guardian.relations.' + r) | translate }}</option> }
          </select>
        </label>
        <label class="cp-field"><span>{{ 'companion.guardian.email' | translate }}</span><input class="form-control" type="email" [ngModel]="gEmail()" (ngModelChange)="gEmail.set($event)" /></label>
      </div>
      <div class="cp-actions">
        <button cButton color="primary" [disabled]="busy() || !gName().trim() || !gEmail().trim()" (click)="requestGuardian()">{{ 'companion.guardian.send' | translate }}</button>
      </div>
      <p class="cp-hint mt-2 mb-0">{{ 'companion.guardian.mail_hint' | translate }}</p>
    }
  </div>
}

<!-- 3 · The profile steps / form -->
@if (profile()?.age_verified && !minorLocked()) {
  <div class="cp-card">
    @if (!confirmed()) {
      <p class="cp-hint mb-1">{{ 'companion.profile.step_counter' | translate:{ n: stepNumber(), total: stepTotal() } }}</p>
      <div class="steps">
        @for (s of steps; track s) { @if (s !== 'age') { <span [class.on]="step() === s">{{ ('companion.profile.steps.' + s) | translate }}</span> } }
      </div>
      @if (step() === 'goals') {
        <p class="mb-3">{{ 'companion.profile.goals.intro' | translate }}</p>
      }
    } @else {
      <h2 class="cp-h">{{ 'companion.profile.title' | translate }}</h2>
      @if (profile()?.stale) { <div class="alert alert-secondary">{{ 'companion.profile.stale' | translate }}</div> }
    }

    @if (step() === 'goals' || confirmed()) {
      <h3 class="cp-h">{{ 'companion.profile.goals.title' | translate }}</h3>
      <p class="cp-hint">{{ 'companion.profile.goals.hint' | translate }}</p>
      @for (g of goals(); track $index; let i = $index) {
        <div class="goal">
          <select class="form-select" [ngModel]="g.kind" (ngModelChange)="setGoal(i, { kind: $event })" [disabled]="readOnly()">
            @for (k of allowedGoalKinds(); track k) { <option [value]="k">{{ ('companion.profile.goals.kinds.' + k) | translate }}</option> }
          </select>
          <input class="form-control" type="text" [placeholder]="'companion.profile.goals.placeholder' | translate" [ngModel]="g.text" (ngModelChange)="setGoal(i, { text: $event })" [disabled]="readOnly()" />
          <button type="button" class="btn btn-sm btn-link text-danger" [disabled]="readOnly()" (click)="removeGoal(i)">✕</button>
        </div>
      }
      @if (!readOnly() && goals().length < 6) { <button type="button" class="btn btn-sm btn-link px-0" (click)="addGoal()">+ {{ 'companion.profile.goals.add' | translate }}</button> }
      @if (profile()?.minor) { <p class="cp-hint">{{ 'companion.profile.goals.minor_hint' | translate }}</p> }
    }

    @if (step() === 'baseline' || confirmed()) {
      <h3 class="cp-h mt-3">{{ 'companion.profile.baseline.title' | translate }}</h3>
      <textarea class="form-control" rows="3" [placeholder]="'companion.profile.baseline.placeholder' | translate" [ngModel]="baselineText()" (ngModelChange)="baselineText.set($event)" [disabled]="readOnly()"></textarea>
    }

    @if (step() === 'time' || confirmed()) {
      <h3 class="cp-h mt-3">{{ 'companion.profile.time.title' | translate }}</h3>
      <div class="grid2">
        <label class="cp-field"><span>{{ 'companion.profile.time.daily' | translate }}</span><input class="form-control" type="number" min="0" max="1440" [ngModel]="dailyMinutes()" (ngModelChange)="dailyMinutes.set($event)" [disabled]="readOnly()" /></label>
        <label class="cp-field"><span>{{ 'companion.profile.time.weekday' | translate }}</span><input class="form-control" type="number" min="0" max="1440" [ngModel]="weekdayMinutes()" (ngModelChange)="weekdayMinutes.set($event)" [disabled]="readOnly()" /></label>
      </div>
    }

    @if (step() === 'movement' || confirmed()) {
      <h3 class="cp-h mt-3">{{ 'companion.profile.movement.title' | translate }}</h3>
      <textarea class="form-control" rows="2" [placeholder]="'companion.profile.movement.placeholder' | translate" [ngModel]="movement()" (ngModelChange)="movement.set($event)" [disabled]="readOnly()"></textarea>
    }

    @if (step() === 'restrictions' || confirmed()) {
      <h3 class="cp-h mt-3">{{ 'companion.profile.restrictions.title' | translate }}</h3>
      <p class="cp-hint">{{ 'companion.profile.restrictions.hint' | translate }}</p>
      <textarea class="form-control" rows="2" [placeholder]="'companion.profile.restrictions.placeholder' | translate" [ngModel]="restrictions()" (ngModelChange)="restrictions.set($event)" [disabled]="readOnly()"></textarea>
    }

    @if (step() === 'settings' || confirmed()) {
      <h3 class="cp-h mt-3">{{ 'companion.profile.settings.title' | translate }}</h3>
      <div class="grid2">
        <label class="cp-field"><span>{{ 'companion.profile.settings.language' | translate }}</span>
          <select class="form-select" [ngModel]="language()" (ngModelChange)="language.set($event)" [disabled]="readOnly()"><option value="de">Deutsch</option><option value="en">English</option></select></label>
        <label class="cp-field"><span>{{ 'companion.profile.settings.time_zone' | translate }}</span>
          <select class="form-select" [ngModel]="timeZone()" (ngModelChange)="timeZone.set($event)" [disabled]="readOnly()">
            @if (!zones.includes(timeZone())) { <option [value]="timeZone()">{{ timeZone() }}</option> }
            @for (z of zones; track z) { <option [value]="z">{{ z }}</option> }
          </select></label>
        <label class="cp-field"><span>{{ 'companion.profile.settings.weight_unit' | translate }}</span>
          <select class="form-select" [ngModel]="weightUnit()" (ngModelChange)="weightUnit.set($event)" [disabled]="readOnly()"><option value="kg">kg</option><option value="lb">lb</option></select></label>
        <label class="cp-field"><span>{{ 'companion.profile.settings.volume_unit' | translate }}</span>
          <select class="form-select" [ngModel]="volumeUnit()" (ngModelChange)="volumeUnit.set($event)" [disabled]="readOnly()"><option value="ml">ml</option><option value="l">l</option><option value="fl_oz">fl oz</option></select></label>
        <label class="cp-field"><span>{{ 'companion.profile.settings.height' | translate }}</span><input class="form-control" type="number" step="0.5" min="50" max="260" [ngModel]="height()" (ngModelChange)="height.set($event)" [disabled]="readOnly()" /></label>
      </div>
      <p class="cp-hint">{{ 'companion.profile.settings.units_hint' | translate }}</p>
    }

    @if (step() === 'review' && !confirmed()) {
      <h3 class="cp-h">{{ 'companion.profile.review.title' | translate }}</h3>
      <p class="cp-hint">{{ 'companion.profile.review.hint' | translate }}</p>
      <dl class="row small">
        <dt class="col-sm-4">{{ 'companion.profile.goals.title' | translate }}</dt><dd class="col-sm-8">@for (g of goals(); track $index) { <div>{{ ('companion.profile.goals.kinds.' + g.kind) | translate }}: {{ g.text }}</div> }</dd>
        <dt class="col-sm-4">{{ 'companion.profile.baseline.title' | translate }}</dt><dd class="col-sm-8">{{ baselineText() || '—' }}</dd>
        <dt class="col-sm-4">{{ 'companion.profile.time.title' | translate }}</dt><dd class="col-sm-8">{{ dailyMinutes() || '—' }} / {{ weekdayMinutes() || '—' }} min</dd>
        <dt class="col-sm-4">{{ 'companion.profile.movement.title' | translate }}</dt><dd class="col-sm-8">{{ movement() || '—' }}</dd>
        <dt class="col-sm-4">{{ 'companion.profile.restrictions.title' | translate }}</dt><dd class="col-sm-8">{{ restrictions() || '—' }}</dd>
        <dt class="col-sm-4">{{ 'companion.profile.settings.title' | translate }}</dt><dd class="col-sm-8">{{ language() }} · {{ timeZone() }} · {{ weightUnit() }} · {{ volumeUnit() }}{{ height() ? ' · ' + height() + ' cm' : '' }}</dd>
      </dl>
    }

    @if (!readOnly()) {
      <div class="cp-actions">
        @if (!confirmed()) {
          @if (step() !== 'goals') { <button cButton color="secondary" variant="outline" [disabled]="busy()" (click)="back()">{{ 'companion.back' | translate }}</button> }
          @if (step() !== 'review') {
            <button cButton color="primary" [disabled]="busy() || !canContinue()" (click)="next()">{{ 'companion.continue' | translate }}</button>
            @if (!canContinue()) { <span class="cp-hint align-self-center">{{ 'companion.profile.goals.required_hint' | translate }}</span> }
          } @else {
            @for (s of ['goals', 'baseline', 'time', 'movement', 'restrictions', 'settings']; track s) {
              <button cButton color="secondary" variant="ghost" size="sm" (click)="step.set($any(s))">{{ ('companion.profile.steps.' + s) | translate }}</button>
            }
            <button cButton color="primary" [disabled]="busy()" (click)="confirm()">{{ 'companion.profile.review.confirm' | translate }}</button>
          }
        } @else {
          <button cButton color="primary" [disabled]="busy()" (click)="save()">{{ 'companion.save' | translate }}</button>
          @if (saved()) { <span class="text-success align-self-center">{{ 'companion.saved' | translate }}</span> }
        }
      </div>
    }
  </div>
}

@if (profile()?.notes?.length) {
  <div class="cp-card">
    <h2 class="cp-h">{{ 'companion.profile.notes.title' | translate }}</h2>
    <p class="cp-hint">{{ 'companion.profile.notes.hint' | translate }}</p>
    <ul class="mb-0">@for (n of profile()!.notes; track $index) { <li>{{ n.text }} <span class="cp-hint">({{ n.recorded_on }})</span></li> }</ul>
  </div>
}
